import React, { useState } from 'react';

const RecipeForm = React.memo(({ onRecipeAdded, api, userId }) => {
    const [image, setImage] = useState(null);
    const [referenceUrl, setReferenceUrl] = useState('');
    const [originalRecipeText, setOriginalRecipeText] = useState(''); // レシピ全文を追加
    const [userMemo, setUserMemo] = useState('');
    const [recipeDate, setRecipeDate] = useState(''); // 日付を追加
    const [isPublic, setIsPublic] = useState(true); // 公開設定を追加
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // ユーザーメモは必須。参考URLまたはオリジナルレシピテキストのどちらかは必須。
        if (!userMemo || (!referenceUrl && !originalRecipeText)) {
            setError('調整メモは必須です。また、参考URLまたはレシピのどちらかが必要です。');
            setLoading(false);
            return;
        }

        let imageData = null;
        if (image) {
            // 画像をBase64エンコード
            imageData = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(image);
            });
        }

        const postData = {
            user_id: userId, // ユーザーIDを追加
            reference_url: referenceUrl,
            original_recipe_text: originalRecipeText, // レシピ全文を追加
            user_memo: userMemo,
            recipe_date: recipeDate, // 日付を追加
            is_public: isPublic, // 公開設定を追加
            image: imageData, // Base64エンコードされた画像データ
        };

        try {
            const response = await api.post('/recipes', postData, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setSuccess('レシピの登録を受け付けました。AIによる処理がバックグラウンドで進行中です。');
            setImage(null);
            setReferenceUrl('');
            setOriginalRecipeText(''); // レシピ全文をクリア
            setUserMemo('');
            setRecipeDate(''); // 日付をクリア
            setIsPublic(true); // 公開設定をクリア
            // 親コンポーネントに新しいレシピが追加されたことを通知
            if (onRecipeAdded) {
                onRecipeAdded(response.data);
            }
        } catch (err) {
            console.error('レシピ登録エラー:', err);
            setError('レシピの登録中にエラーが発生しました。');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginBottom: '40px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
            <h2>新しいレシピを登録</h2>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="image" style={{ display: 'block', marginBottom: '5px' }}>料理写真:</label>
                    <input
                        type="file"
                        id="image"
                        accept="image/*"
                        onChange={(e) => setImage(e.target.files[0])}
                        disabled={loading}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="recipeDate" style={{ display: 'block', marginBottom: '5px' }}>作成日:</label>
                    <input
                        type="date"
                        id="recipeDate"
                        value={recipeDate}
                        onChange={(e) => setRecipeDate(e.target.value)}
                        disabled={loading}
                        style={{ width: 'auto', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        required
                    />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="referenceUrl" style={{ display: 'block', marginBottom: '5px' }}>参考URL:</label>
                    <input
                        type="url"
                        id="referenceUrl"
                        value={referenceUrl}
                        onChange={(e) => setReferenceUrl(e.target.value)}
                        placeholder="例: https://cookpad.com/recipe/1234567"
                        disabled={loading}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="originalRecipeText" style={{ display: 'block', marginBottom: '5px' }}>レシピ:</label>
                    <textarea
                        id="originalRecipeText"
                        value={originalRecipeText}
                        onChange={(e) => setOriginalRecipeText(e.target.value)}
                        placeholder="例: 材料、作り方など、レシピの全文をここに貼り付けてください。"
                        rows="4"
                        disabled={loading}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    ></textarea>
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="userMemo" style={{ display: 'block', marginBottom: '5px' }}>調整メモ:</label>
                    <textarea
                        id="userMemo"
                        value={userMemo}
                        onChange={(e) => setUserMemo(e.target.value)}
                        placeholder="例: 鶏肉を豚肉に変更、辛さ控えめに、野菜を追加したい"
                        rows="4"
                        required
                        disabled={loading}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    ></textarea>
                </div>
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                    <label style={{ marginRight: '10px' }}>公開設定:</label>
                    <input
                        type="radio"
                        id="isPublicOk"
                        name="isPublic"
                        value="true"
                        checked={isPublic === true}
                        onChange={() => setIsPublic(true)}
                        disabled={loading}
                        style={{ marginRight: '5px' }}
                    />
                    <label htmlFor="isPublicOk" style={{ marginRight: '15px' }}>OK</label>

                    <input
                        type="radio"
                        id="isPublicNg"
                        name="isPublic"
                        value="false"
                        checked={isPublic === false}
                        onChange={() => setIsPublic(false)}
                        disabled={loading}
                        style={{ marginRight: '5px' }}
                    />
                    <label htmlFor="isPublicNg">NG</label>
                </div>
                <button type="submit" disabled={loading} style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '16px'
                }}>
                    {loading ? '登録中...' : 'レシピを登録'}
                </button>
            </form>
            {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
            {success && <p style={{ color: 'green', marginTop: '10px' }}>{success}</p>}
        </div>
    );
}); // ここに閉じ括弧とセミコロンを追加

export default RecipeForm;
